import React, { useMemo, useState } from 'react';
import {
  Link,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';
import {
  AlertCircle,
  CheckCircle2,
  Eye,
  EyeOff,
  Loader2,
  LockKeyhole,
} from 'lucide-react';

import { routeConstants } from '../../../constants/routeConstants';
import {
  authApi,
  getAuthErrorMessage,
} from '../services/authApi';

const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const uid = searchParams.get('uid') || '';
  const token = searchParams.get('token') || '';
  const purpose = searchParams.get('purpose') || 'reset';

  const isActivation = purpose === 'activation';
  const hasValidLinkData = Boolean(uid && token);

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] =
    useState('');

  const [showNewPassword, setShowNewPassword] =
    useState(false);

  const [showConfirmPassword, setShowConfirmPassword] =
    useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [completed, setCompleted] = useState(false);

  const pageContent = useMemo(() => {
    if (isActivation) {
      return {
        title: 'Activate your account',
        description:
          'Create a secure password to activate your School ERP login.',
        buttonText: 'Activate account',
        successTitle: 'Account activated',
        successMessage:
          'Your account is active and your password has been created successfully.',
      };
    }

    return {
      title: 'Reset your password',
      description:
        'Enter a new secure password for your School ERP account.',
      buttonText: 'Reset password',
      successTitle: 'Password reset successful',
      successMessage:
        'Your password has been changed successfully. You can now log in with your new password.',
    };
  }, [isActivation]);

  const validateForm = () => {
    if (!hasValidLinkData) {
      return 'The password reset link is invalid or incomplete.';
    }

    if (newPassword.length < 8) {
      return 'Password must contain at least 8 characters.';
    }

    if (newPassword !== confirmPassword) {
      return 'The passwords do not match.';
    }

    return '';
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const validationError = validateForm();

    if (validationError) {
      setError(validationError);
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      await authApi.resetPassword({
        uid,
        token,
        newPassword,
        confirmPassword,
      });

      setCompleted(true);
      setNewPassword('');
      setConfirmPassword('');
    } catch (requestError) {
      setError(
        getAuthErrorMessage(
          requestError,
          'The reset link is invalid or has expired.',
        ),
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (!hasValidLinkData) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4 py-12">
        <div className="w-full max-w-md rounded-2xl border border-gray-200 bg-white p-8 text-center shadow-sm">
          <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-red-100 text-red-600">
            <AlertCircle className="h-7 w-7" />
          </div>

          <h1 className="text-2xl font-bold text-gray-900">
            Invalid reset link
          </h1>

          <p className="mt-3 text-sm leading-6 text-gray-500">
            This password reset link is incomplete. Request a
            new link from the forgot-password page.
          </p>

          <Link
            to={routeConstants.FORGOT_PASSWORD}
            className="mt-6 inline-flex rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-blue-700"
          >
            Request new link
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4 py-12">
      <div className="w-full max-w-md">
        <div className="mb-6 text-center">
          <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-blue-100 text-blue-600">
            <LockKeyhole className="h-7 w-7" />
          </div>

          <h1 className="text-3xl font-bold text-gray-900">
            {pageContent.title}
          </h1>

          <p className="mt-2 text-sm leading-6 text-gray-500">
            {pageContent.description}
          </p>
        </div>

        <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm sm:p-8">
          {completed ? (
            <div>
              <div className="flex items-start gap-3 rounded-lg border border-green-200 bg-green-50 p-4 text-green-700">
                <CheckCircle2 className="mt-0.5 h-6 w-6 shrink-0" />

                <div>
                  <h2 className="font-semibold">
                    {pageContent.successTitle}
                  </h2>

                  <p className="mt-1 text-sm leading-6">
                    {pageContent.successMessage}
                  </p>
                </div>
              </div>

              <button
                type="button"
                onClick={() => navigate(routeConstants.LOGIN)}
                className="mt-6 w-full rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-blue-700"
              >
                Go to login
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              {error && (
                <div className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                  <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <div>
                <label
                  htmlFor="new-password"
                  className="mb-1.5 block text-sm font-medium text-gray-700"
                >
                  New password
                </label>

                <div className="relative">
                  <input
                    id="new-password"
                    type={showNewPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    required
                    minLength={8}
                    value={newPassword}
                    onChange={(event) => {
                      setNewPassword(event.target.value);

                      if (error) {
                        setError('');
                      }
                    }}
                    placeholder="Minimum 8 characters"
                    className="w-full rounded-lg border border-gray-300 px-3 py-2.5 pr-11 text-sm outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                  />

                  <button
                    type="button"
                    onClick={() =>
                      setShowNewPassword((current) => !current)
                    }
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    aria-label={
                      showNewPassword
                        ? 'Hide password'
                        : 'Show password'
                    }
                  >
                    {showNewPassword ? (
                      <EyeOff className="h-5 w-5" />
                    ) : (
                      <Eye className="h-5 w-5" />
                    )}
                  </button>
                </div>
              </div>

              <div>
                <label
                  htmlFor="confirm-password"
                  className="mb-1.5 block text-sm font-medium text-gray-700"
                >
                  Confirm password
                </label>

                <div className="relative">
                  <input
                    id="confirm-password"
                    type={
                      showConfirmPassword ? 'text' : 'password'
                    }
                    autoComplete="new-password"
                    required
                    minLength={8}
                    value={confirmPassword}
                    onChange={(event) => {
                      setConfirmPassword(event.target.value);

                      if (error) {
                        setError('');
                      }
                    }}
                    placeholder="Enter the same password again"
                    className="w-full rounded-lg border border-gray-300 px-3 py-2.5 pr-11 text-sm outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                  />

                  <button
                    type="button"
                    onClick={() =>
                      setShowConfirmPassword(
                        (current) => !current,
                      )
                    }
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    aria-label={
                      showConfirmPassword
                        ? 'Hide password'
                        : 'Show password'
                    }
                  >
                    {showConfirmPassword ? (
                      <EyeOff className="h-5 w-5" />
                    ) : (
                      <Eye className="h-5 w-5" />
                    )}
                  </button>
                </div>
              </div>

              <div className="rounded-lg bg-gray-50 p-3 text-xs leading-5 text-gray-500">
                Use at least 8 characters. A combination of
                uppercase letters, lowercase letters, numbers,
                and symbols is recommended.
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="flex w-full items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {submitting && (
                  <Loader2 className="h-4 w-4 animate-spin" />
                )}

                {submitting
                  ? 'Saving password...'
                  : pageContent.buttonText}
              </button>

              <Link
                to={routeConstants.LOGIN}
                className="block text-center text-sm font-semibold text-blue-600 hover:text-blue-700"
              >
                Back to login
              </Link>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;